import Spinner from "@/components/chat/left-column/feedback/Spinner";
import { Button } from "@/components/primitives/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/primitives/form";
import { Input } from "@/components/primitives/input";
import { FormStepProps } from "@/components/shared/Wizard/Wizard";
import { MAX_WORD_LENGTH, PHRASE_WORDS_COUNT } from "@/config/constants";
import { useToast } from "@/hooks/useToast";
import { getEncryptedPasswordForUser } from "@/middleware/forgotPassword";
import {
  getUsernameFormSchema,
  UsernameFormType,
} from "@/schemas/forgotPassword";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

export type UsernameStepData = {
  username: string;
  encryptedPassword: string;
};

export default function UsernameStep({
  onNext,
  onClose,
  updateData,
}: FormStepProps<UsernameStepData>) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const form = useForm<UsernameFormType>({
    resolver: zodResolver(getUsernameFormSchema(t)),
    defaultValues: {
      username: "",
    },
  });

  async function onSubmit(usernameData: UsernameFormType) {
    try {
      setLoading(true);
      const encryptedPassword = await getEncryptedPasswordForUser(
        usernameData.username,
      );
      updateData({ ...usernameData, encryptedPassword: encryptedPassword });

      onNext();
    } catch (error) {
      toast({
        duration: 1000,
        description:
          error instanceof Error ? error.message : t("errorOccurred"),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }
  return (
    <div className="flex size-full flex-col items-stretch justify-center gap-4">
      <h1 className="text-lg">{t("forgot.title")}</h1>
      <p className="opacity-50">
        {t("forgot.note", { count: PHRASE_WORDS_COUNT })}
      </p>
      <p className="opacity-50">{t("forgot.description")}</p>
      <Form {...form}>
        <form
          className="mt-4 flex flex-col gap-4"
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <FormField
            control={form.control}
            name="username"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("forgot.username")}</FormLabel>
                <FormControl>
                  <Input
                    className="flex-1 rounded-lg border-border bg-card py-6 placeholder:opacity-70"
                    type="text"
                    autoComplete="username"
                    autoFocus
                    maxLength={MAX_WORD_LENGTH}
                    disabled={form.formState.isSubmitting || loading}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          {form.formState.isSubmitting && <Spinner />}

          <div className="flex items-center gap-4 self-end">
            <Button type="button" variant="outline" onClick={onClose}>
              {t("cancel")}
            </Button>
            <Button
              type="submit"
              disabled={form.formState.isSubmitting || loading}
            >
              {t("next")}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}
